/**
 * What the lights are ACTUALLY doing - the one genuinely closed loop in this
 * app.
 *
 * ## Why this is different from everything else here
 *
 * OPButtons spends most of its life unable to know whether anything worked.
 * A MIDI send tells us OPBridge accepted three bytes; whether Ableton did
 * anything with them is invisible, which is why DEC-124 draws such a careful
 * line under "we sent it" and forbids any UI implying "it is on".
 *
 * DMX is not like that. `GET /dmx/fixtures/resolved` reverse-resolves the
 * LIVE DMX buffer, so it reports the bytes genuinely on the wire, plus
 * OPBridge's ownership model on top: who is holding each capability, whether
 * that hold is stale, whether the fixture is parked. This is the one surface
 * where a control can honestly say what IS rather than what was asked for.
 *
 * The most valuable thing it carries is not the colour. It is `heldBy`. A
 * fixture held by `playback` is being driven by a running timeline or effect,
 * and a Light Colour tap against it can look like it did nothing. Without
 * this, that reads as a broken button; with it, the control can say who has
 * the light. That is precisely the class of failure the "empty rig must never
 * be indistinguishable from an unreachable one" rule exists to prevent.
 *
 * ## POLLED, unlike the rig and the catalogue, and gated three ways
 *
 * `/fixtures` and `/dmx/profiles` are read on connect and never polled,
 * because a rig changes when it is repatched. This is the opposite: it is
 * live performance state, so there is no event to hang it on and polling is
 * the only honest option. Polling on a gig iPad is a real cost - steady LAN
 * traffic and battery for a whole night - so it is gated hard:
 *
 *   1. **Only when something is watching.** `setLightingWatchers` is
 *      reference-counted by the controls themselves via `watchLighting()`. No
 *      lighting control mounted means no request, ever. A page of MIDI
 *      buttons costs nothing.
 *   2. **Only when a bridge is connected.** Nothing to ask otherwise.
 *   3. **Only when the document is visible.** An iPad in a bag must not be
 *      polling a LAN endpoint once a second all night. `visibilitychange`
 *      stops and restarts it.
 *
 * ## 1000ms, and why not faster
 *
 * A human reading a badge cannot use faster, and this is a DISPLAY surface -
 * nothing schedules or fires off it. One request per second against a rig of
 * five is trivial for OPBridge (the route is on a 50 r/s bucket) and invisible
 * on a LAN. Going to 100ms would make a strobe's flicker visible in the UI,
 * which is a novelty that costs ten times the battery to show something the
 * operator can already see on the stage.
 *
 * ## A stale read says so
 *
 * `lightingStateAge()` exposes how long ago the last successful read landed.
 * A control showing a colour from twelve seconds ago while the bridge is gone
 * is lying more quietly than one showing nothing, so the consumers fall back
 * to "unknown" past a threshold rather than holding the last good value
 * forever. Same reasoning as the CP-132 badge that read CONNECTED through 147
 * seconds offline.
 */

import { createSignal, createEffect, onCleanup, untrack, type Accessor } from 'solid-js';
import { bridges, getConnection } from '../opbridge/store.ts';
import { OpbridgeError, type DmxResolvedFixture } from '../opbridge/httpClient.ts';
import { watchJob } from '../diag/watchdog.ts';

/** Poll period. See the doc comment for why this is not faster. */
const POLL_MS = 1000;

/** Past this, the last read is no longer presented as current. Three missed
 *  polls: one miss is a hiccup, three is a pattern. */
const STALE_AFTER_MS = 3500;

const [mapSig, setMap] = createSignal<Record<string, DmxResolvedFixture>>({});
const [fetchedAtSig, setFetchedAt] = createSignal(0);
const [errorSig, setError] = createSignal<string>('');
const [watchers, setWatchers] = createSignal(0);
/** Bumped on every successful read. Consumers depend on THIS rather than on
 *  `mapSig`, because a Solid dependency on an object read through a lookup
 *  does not re-run when the object is replaced - the exact trap that made a
 *  connection badge stick for 147 seconds (CP-132). */
const [version, setVersion] = createSignal(0);

export const lightingStateVersion: Accessor<number> = version;
export const lightingStateError: Accessor<string> = errorSig;

/** How long ago the last SUCCESSFUL read landed, in ms. `Infinity` before the
 *  first one. */
export function lightingStateAge(): number {
  version(); // dependency: re-evaluates whenever a read lands
  const at = fetchedAtSig();
  return at === 0 ? Infinity : Date.now() - at;
}

/** True when the last read is recent enough to present as current. */
export function lightingStateFresh(): boolean {
  return lightingStateAge() <= STALE_AFTER_MS;
}

/**
 * The live state of one fixture, or undefined when unknown.
 *
 * Reads `version()` FIRST and deliberately. `mapSig()[id]` alone looks like a
 * tracked read and is not one in the way that matters: the returned object is
 * replaced wholesale on each poll, and a component that captured it would
 * hold the old one forever. Reading the version signal makes the dependency
 * explicit. This is the documented Solid trap from CP-132, applied before it
 * could bite rather than after.
 */
export function liveFixture(id: string): DmxResolvedFixture | undefined {
  version();
  return mapSig()[id];
}

/** Who is holding this fixture, when it is someone other than us. Returns
 *  undefined when nothing holds it, when WE hold it, or when the state is
 *  stale - all three of which mean "there is nothing useful to warn about".
 *
 *  `"opbuttons"` is filtered because a control reporting that OPButtons holds
 *  the light it just set would be noise on every single tap. */
export function heldByOther(id: string): string | undefined {
  if (!lightingStateFresh()) return undefined;
  const f = liveFixture(id);
  if (!f?.held) return undefined;
  const by = f.heldBy?.trim();
  if (!by || by === 'opbuttons') return undefined;
  return by;
}

/** True when the fixture is PARKED (DEC-482): deliberately held and excluded
 *  from playback until an explicit release. Distinct from an ordinary hold -
 *  a parked fixture ignores a timeline, which is a much stronger statement
 *  and worth its own badge. */
export function isParked(id: string): boolean {
  if (!lightingStateFresh()) return false;
  return liveFixture(id)?.parked === true;
}

/** The colour actually on the wire, as `#rrggbb`, or undefined when unknown
 *  or stale. A dark fixture returns `#000000` rather than undefined - "off"
 *  is a real answer, and collapsing it into "unknown" is the same mistake as
 *  an empty rig reading like an unreachable one. */
export function liveColorHex(id: string): string | undefined {
  const f = liveFixture(id);
  // No colour from OPBridge (a fixture another computer owns, read before its state arrived) is unknown, not black.
  if (!f || !f.rgb || !lightingStateFresh()) return undefined;
  const to = (v: number) => Math.max(0, Math.min(255, Math.round(v))).toString(16).padStart(2, '0');
  return `#${to(f.rgb.r)}${to(f.rgb.g)}${to(f.rgb.b)}`;
}

/**
 * The live level of a fixture, 0-255.
 *
 * NOT simply `intensity`. On a fixture with a real dimmer channel the level
 * IS `intensity`; on a dimmer-less one (the SlimPARs, the COLORband)
 * OPBridge bakes the level into the RGB and leaves `intensity` at 255, so
 * reading `intensity` there would report full brightness for a fixture sitting
 * at a fifth. Measured: `{rgb:(255,0,0), level:200}` on a SlimPAR reads back
 * `rgb=(200,0,0) intensity=255`.
 *
 * So the honest reading is the BRIGHTEST CHANNEL when the fixture has no
 * dimmer, and `intensity` when it does. We cannot tell which kind it is from
 * this payload alone, so the rule is: take the smaller of the two readings.
 * On a dimmer fixture at half with a full-red colour that is `intensity`
 * (128), correct. On a dimmer-less fixture baked to a fifth that is the
 * channel max (51), correct. The failure mode of guessing wrong in the other
 * direction - reporting 255 for a fixture that is nearly dark - is the one
 * that would make the readout useless.
 */
export function liveLevel(id: string): number | undefined {
  const f = liveFixture(id);
  if (!f || !lightingStateFresh()) return undefined;
  if (!f.on) return 0;
  // Unknown as in `liveColorHex`: no reading to take the smaller of.
  if (!f.rgb || f.intensity === null) return undefined;
  const channelMax = Math.max(f.rgb.r, f.rgb.g, f.rgb.b);
  return Math.min(f.intensity, channelMax === 0 ? f.intensity : channelMax);
}

async function pollOnce(): Promise<void> {
  watchJob('lights-poll');
  const primary = bridges().find((b) => b.is_primary) ?? bridges()[0];
  const conn = primary ? getConnection(primary.id) : undefined;
  if (!conn || conn.status() !== 'connected') return;
  try {
    const res = await conn.httpClient().dmxResolvedState();
    const next: Record<string, DmxResolvedFixture> = {};
    for (const f of Array.isArray(res.fixtures) ? res.fixtures : []) {
      if (f?.id) next[f.id] = f;
    }
    setMap(next);
    setFetchedAt(Date.now());
    setError('');
    setVersion((v) => v + 1);
  } catch (err) {
    // Deliberately does NOT clear the map. The age check is what decides
    // whether the last values may still be shown; wiping them here would make
    // a single dropped packet look identical to a fixture that went dark.
    setError(err instanceof OpbridgeError ? `${err.kind}: ${err.message}` : (err as Error).message);
  }
}

/**
 * Register interest in live lighting state for the lifetime of the calling
 * component. Reference-counted: the poll runs while at least one control is
 * mounted and stops when the last one unmounts.
 *
 * Call it at the top level of a lighting component. `onCleanup` handles the
 * decrement, so nothing has to remember to unregister.
 */
export function watchLighting(): void {
  setWatchers((n) => n + 1);
  onCleanup(() => setWatchers((n) => Math.max(0, n - 1)));
}

/**
 * Install the poll loop. Called once from App.tsx.
 *
 * `untrack` around the body so the signals written by `pollOnce` cannot
 * re-trigger this effect - the standing Solid trap of an effect that writes a
 * signal it also reads, which here would produce an accelerating request
 * storm rather than a mere double-render.
 */
export function installLightingLiveState(): void {
  let timer: number | undefined;
  let visible = typeof document === 'undefined' || document.visibilityState === 'visible';

  const stop = () => {
    if (timer !== undefined) { clearInterval(timer); timer = undefined; }
  };
  const start = () => {
    if (timer !== undefined) return;
    void pollOnce();
    timer = window.setInterval(() => { void pollOnce(); }, POLL_MS);
  };

  const onVisibility = () => {
    visible = document.visibilityState === 'visible';
    // Re-evaluate immediately rather than waiting for the next effect run:
    // coming back from a locked iPad should refresh at once, not in a second.
    if (!visible) stop();
    else if (untrack(() => watchers()) > 0 && untrack(anyConnected)) start();
  };
  if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', onVisibility);
    onCleanup(() => document.removeEventListener('visibilitychange', onVisibility));
  }

  createEffect(() => {
    const want = watchers() > 0 && anyConnected();
    untrack(() => {
      if (want && visible) start();
      else stop();
    });
  });

  onCleanup(stop);
}

function anyConnected(): boolean {
  return bridges().some((b) => getConnection(b.id)?.status() === 'connected');
}

// ─── DEV test hook ────────────────────────────────────────────────────────
// Same reasoning as the other lighting hooks: a smoke must read the SAME live
// module instance the app uses, never a `page.evaluate(() => import())`
// re-import, which resolves to a second dead copy once Vite has appended an
// HMR version query.
declare global {
  interface Window {
    __opbuttonsLighting?: {
      watchers(): number;
      ageMs(): number;
      fresh(): boolean;
      version(): number;
      error(): string;
      colorOf(id: string): string | undefined;
      levelOf(id: string): number | undefined;
      heldByOther(id: string): string | undefined;
      parked(id: string): boolean;
    };
  }
}

if (typeof window !== 'undefined' && import.meta.env.DEV) {
  window.__opbuttonsLighting = {
    watchers: () => watchers(),
    ageMs: lightingStateAge,
    fresh: lightingStateFresh,
    version: () => version(),
    error: () => errorSig(),
    colorOf: liveColorHex,
    levelOf: liveLevel,
    heldByOther,
    parked: isParked,
  };
}
